import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Grid,
  Paper,
  Stack,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  CurrencyRupee as MoneyIcon,
  History as HistoryIcon,
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  CurrencyRupee as CashIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Assignment as InvoiceIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Science as TestIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  PendingActions as PendingIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

// Import Firebase services
import { PaymentManagementService } from '../services/firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import RazorpayService from '../services/RazorpayService';
import { RazorpayResponse, RazorpayError } from '../types/razorpay';

// Types (converted from React Native structure - matching mobile app exactly)
interface Payment {
  id: string;
  originalId: string;
  patientName: string;
  patientId: string;
  patientType: 'IP' | 'OP' | 'TEST';
  amount: number;
  actualAmountPaid?: number;
  fullAmount?: number;
  totalAmount: number;
  paidAmount: number;
  dueAmount: number;
  status: string;
  paymentStatus: string;
  statusColor: string;
  method: string;
  paymentMethod?: string;
  description: string;
  lastPaymentDate: string;
  registrationDate?: string;
  serviceType: string;
  type: string;
  isLocalPayment?: boolean;
  isPending?: boolean;
  hasSyncFailed?: boolean;
  localIndicator?: string;
  paymentHistory?: Array<any>;
  transactionId?: string;
  appointmentId?: string;
  createdAt?: string;
  updatedAt?: string;
  billBreakdown?: Array<{ item: string; amount: number }>;
  transactionHistory?: Array<{
    type: string;
    amount: number;
    date: string;
    method: string;
    status: string;
  }>;
}

interface PaymentStats {
  totalRevenue: number;
  totalPending: number;
  fullyPaidCount: number;
  partiallyPaidCount: number;
  pendingCount: number;
  totalPatients: number;
}

interface User {
  id: string;
  name: string;
  phone: string;
  email?: string;
}

const Payments: React.FC = () => {
  // State management (matching React Native structure)
  const [firebasePayments, setFirebasePayments] = useState<Payment[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [appointmentPayments, setAppointmentPayments] = useState<Payment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const [refreshing, setRefreshing] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [ipOpPatients, setIpOpPatients] = useState<User[]>([]);
  const [patientsBillingData, setPatientsBillingData] = useState<Map<string, { totalAmount: number; paidAmount: number; dueAmount: number }>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedPatients, setSearchedPatients] = useState<User[]>([]);
  const [showPatientCards, setShowPatientCards] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [razorpayOrderId, setRazorpayOrderId] = useState('');
  const [patientBillingSummary, setPatientBillingSummary] = useState<{
    totalAmount: number;
    totalPaid: number;
    dueAmount: number;
  } | null>(null);
  const [addPaymentForm, setAddPaymentForm] = useState({
    patientType: '',
    patientId: '',
    patientName: '',
    fullAmount: '',
    actualAmountPaid: '',
    type: 'consultation',
    paymentMethod: 'cash',
    description: '',
    transactionId: '',
    razorpayPaymentId: '',
    razorpayOrderId: '',
    razorpaySignature: ''
  });

  // Filter options (matching React Native structure)
  const filterOptions = [
    'All',
    'Due Patients',
    'Fully Paid',
    'Partially Paid', 
    'Pending',
    'OP Payments',
    'IP Payments',
    'Test Payments',
    'General'
  ];

  // Calculate payment statistics - MATCHING MOBILE APP LOGIC
  const paymentStats = useMemo(() => {
    // Merge regular payments with appointment pending payments
    const paymentsToUse = [...(payments || []), ...(appointmentPayments || [])];
    const totalPayments = paymentsToUse.length;
    
    console.log(`📊 Payment stats calculating from ${totalPayments} payments (${payments?.length || 0} regular + ${appointmentPayments?.length || 0} appointments)`);
    
    // ✅ Calculate total revenue from LATEST payment card per patient (cumulative paidAmount)
    // Group payments by patient and get latest card's paidAmount
    const latestPaymentsByPatient = new Map<string, Payment>();
    
    paymentsToUse.forEach(payment => {
      if (!payment.patientId) return;
      
      const existing = latestPaymentsByPatient.get(payment.patientId);
      if (!existing) {
        latestPaymentsByPatient.set(payment.patientId, payment);
      } else {
        // Keep the latest payment (newest date)
        const existingDate = new Date(existing.lastPaymentDate || existing.registrationDate || 0).getTime();
        const currentDate = new Date(payment.lastPaymentDate || payment.registrationDate || 0).getTime();
        
        if (currentDate > existingDate) {
          latestPaymentsByPatient.set(payment.patientId, payment);
        }
      }
    });
    
    // Sum paidAmount from latest payment cards only
    const totalRevenue = Array.from(latestPaymentsByPatient.values()).reduce((sum, payment) => {
      const paidAmount = parseFloat((payment.paidAmount || 0).toString()) || 0;
      return sum + paidAmount;
    }, 0);
    
    console.log(`💰 Total Revenue calculated from ${latestPaymentsByPatient.size} patients' latest payments`);
    
    // Calculate pending dues - Use latest payment's dueAmount per patient (same logic as Total Revenue)
    const totalPending = Array.from(latestPaymentsByPatient.values()).reduce((sum, payment) => {
      const dueAmount = parseFloat((payment.dueAmount || 0).toString()) || 0;
      return sum + dueAmount;
    }, 0);
    
    console.log(`⏳ Pending Dues calculated from ${latestPaymentsByPatient.size} patients' latest payments`);
    
    const fullyPaidCount = paymentsToUse.filter(p => 
      p.status === 'success' || 
      p.status === 'Fully Paid' || 
      p.status === 'paid' || 
      p.paymentStatus === 'Fully Paid' || 
      p.paymentStatus === 'Paid' ||
      p.paymentStatus === 'Completed'
    ).length;
    const pendingCount = paymentsToUse.filter(p => 
      p.status === 'Pending' || p.status === 'pending' || p.paymentStatus === 'Pending'
    ).length;
    const partiallyPaidCount = paymentsToUse.filter(p => 
      p.status === 'Partially Paid' || p.status === 'partial' || p.paymentStatus === 'Partially Paid' || p.paymentStatus === 'partial'
    ).length;
    
    // Count unique patients
    const uniquePatientIds = new Set(paymentsToUse.map(p => p.patientId).filter(id => id));
    const uniquePatientsCount = uniquePatientIds.size;
    
    console.log(`💰 Payment stats calculated: Revenue=₹${totalRevenue}, Pending=₹${totalPending}, FullyPaid=${fullyPaidCount}, Partial=${partiallyPaidCount}, UniquePatients=${uniquePatientsCount}`);

    return {
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalPending: Math.round(totalPending * 100) / 100,
      fullyPaidCount,
      partiallyPaidCount,
      pendingCount,
      totalPatients: uniquePatientsCount,
      opPayments: paymentsToUse.filter(p => p.serviceType === 'OP').length,
      ipPayments: paymentsToUse.filter(p => p.serviceType === 'IP').length,
      testPayments: paymentsToUse.filter(p => p.serviceType === 'TEST').length
    };
  }, [payments, appointmentPayments]);

  // Handle click outside to close patient cards
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-search-container]')) {
        setShowPatientCards(false);
      }
    };

    if (showPatientCards) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPatientCards]);

  // Load payments from Firebase with real-time updates - MATCHING MOBILE APP
  useEffect(() => {
    const setupRealtimePayments = () => {
      try {
        setIsLoading(true);
        console.log('🔄 Setting up real-time payments listener (matching mobile app)...');
        
        // Set up listener for users collection
        const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
          const usersData = snapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name || doc.data().firstName || 'Unknown',
            phone: doc.data().phone || doc.data().mobile || 'N/A',
            email: doc.data().email || ''
          }));
          setUsers(usersData);
          console.log(`🔄 Loaded ${usersData.length} users`);
        }, (error) => {
          console.error('❌ Error in users listener:', error);
        });

        // Load IP/OP patients ONLY from patients collection (single source of truth)
        const unsubscribePatients = onSnapshot(collection(db, 'patients'), (snapshot) => {
          const patientsData: User[] = [];
          const billingData = new Map<string, { totalAmount: number; paidAmount: number; dueAmount: number }>();
          
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            patientsData.push({
              id: doc.id,
              name: data.name || data.fullName || data.firstName || 'Unknown Patient',
              phone: data.phone || data.phoneNumber || data.mobile || 'N/A',
              email: data.email || ''
            });
            
            // Store billing data for each patient
            billingData.set(doc.id, {
              totalAmount: data.totalAmount || 0,
              paidAmount: data.paidAmount || 0,
              dueAmount: data.dueAmount || 0
            });
          });
          
          setIpOpPatients(patientsData);
          setPatientsBillingData(billingData);
          console.log(`✅ Loaded ${patientsData.length} IP/OP patients from patients collection`);
          console.log(`📋 Patient IDs:`, patientsData.map(p => ({ id: p.id, name: p.name })));
        }, (error) => {
          console.error('❌ Error in patients listener:', error);
        });
        
        // Load appointments to include in payments summary
        // Appointments with "Pay at Hospital" that are pending will show as pending dues
        const unsubscribeAppointments = onSnapshot(collection(db, 'appointments'), (snapshot) => {
          const appointmentsWithPendingPayments: Payment[] = [];
          
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            const paymentStatus = data.paymentStatus || 'Pending';
            const paymentMode = data.paymentMode || data.paymentMethod || '';
            const amount = data.fee || data.amount || 0;
            
            // Only include appointments with pending payments
            // Either "Pay at Hospital" with Pending status, or any other pending payment
            if (paymentStatus === 'Pending' && amount > 0) {
              appointmentsWithPendingPayments.push({
                id: `apt_${doc.id}`,
                originalId: doc.id,
                patientName: data.patientName || data.name || 'Unknown',
                patientId: data.patientId || doc.id,
                patientType: 'OP',
                amount: amount,
                totalAmount: amount,
                paidAmount: 0,
                dueAmount: amount,
                status: 'Pending',
                paymentStatus: 'Pending',
                statusColor: '#EF4444',
                method: paymentMode,
                description: `Appointment - ${data.service || data.serviceName || 'Consultation'}`,
                lastPaymentDate: data.appointmentDate || data.date || new Date().toISOString().split('T')[0],
                registrationDate: data.createdAt || new Date().toISOString().split('T')[0],
                serviceType: 'OP',
                type: 'appointment',
                isLocalPayment: false,
                isPending: true,
                appointmentId: doc.id,
                transactionHistory: []
              });
            }
          });
          
          console.log(`📅 Loaded ${appointmentsWithPendingPayments.length} appointments with pending payments`);
          
          // Store appointments separately to merge with payments
          setAppointmentPayments(appointmentsWithPendingPayments);
        }, (error) => {
          console.error('❌ Error in appointments listener:', error);
        });
        
        const q = query(collection(db, 'payments'));
        const unsubscribePayments = onSnapshot(q, (querySnapshot) => {
          const paymentsData: Payment[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            
            // Normalize status to handle case variations - EXACT match to mobile app
            let normalizedStatus = data.status || data.paymentStatus || 'Pending';
            const statusLower = normalizedStatus.toLowerCase();
            if (statusLower === 'paid' || statusLower === 'fully paid' || statusLower === 'completed' || statusLower === 'success') {
              normalizedStatus = 'Fully Paid';
            } else if (statusLower === 'partial' || statusLower === 'partially paid') {
              normalizedStatus = 'Partially Paid';
            } else if (statusLower === 'pending') {
              normalizedStatus = 'Pending';
            } else {
              // If status is not recognized but amount exists, treat as paid
              normalizedStatus = (data.amount && data.amount > 0) ? 'Fully Paid' : 'Pending';
            }
            
            // ✅ Calculate proper amounts from payment record (will be updated with patient data in useEffect)
            const paymentAmount = data.amount || data.paidAmount || data.actualAmountPaid || 0;
            const totalBillAmount = data.totalAmount || data.fullAmount || paymentAmount;
            const amountPaid = data.paidAmount || data.actualAmountPaid || data.amount || 0;
            const amountDue = data.dueAmount || Math.max(0, totalBillAmount - amountPaid);
            
            paymentsData.push({
              id: doc.id,
              originalId: data.originalId || doc.id,
              patientName: data.patientName || '',
              patientId: data.patientId || '',
              patientType: data.patientType || 'OP',
              amount: paymentAmount,
              totalAmount: totalBillAmount,
              paidAmount: amountPaid,
              dueAmount: amountDue,
              status: normalizedStatus,
              paymentStatus: normalizedStatus,
              statusColor: data.statusColor || '#EF4444',
              method: data.method || data.paymentMethod || 'cash',
              description: data.description || '',
              lastPaymentDate: data.lastPaymentDate || data.paymentDate || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
              registrationDate: data.registrationDate || data.createdAt?.toDate?.()?.toISOString().split('T')[0],
              serviceType: data.serviceType || data.patientType || 'OP',
              type: data.type || 'general',
              isLocalPayment: data.isLocalPayment || false,
              isPending: data.isPending || false,
              hasSyncFailed: data.hasSyncFailed || false,
              localIndicator: data.localIndicator,
              billBreakdown: data.billBreakdown || [],
              transactionHistory: data.transactionHistory || [],
              transactionId: data.transactionId || ''
            });
          });
          
          setFirebasePayments(paymentsData);
          setPayments(paymentsData);
          setIsLoading(false);
          console.log(`🔄 Real-time update: ${paymentsData.length} payments from Firebase`);
        }, (error) => {
          console.error('❌ Error in payments listener:', error);
          setIsLoading(false);
          setSnackbar({ 
            open: true, 
            message: 'Failed to load payments data', 
            severity: 'error' 
          });
        });
        
        // Return cleanup function to unsubscribe all listeners
        return () => {
          console.log('🧹 Cleaning up all listeners...');
          unsubscribeUsers();
          unsubscribePatients();
          unsubscribeAppointments();
          unsubscribePayments();
        };
      } catch (error) {
        console.error('❌ Error setting up real-time listener:', error);
        setIsLoading(false);
        setSnackbar({ 
          open: true, 
          message: 'Failed to setup real-time listener', 
          severity: 'error' 
        });
        return () => {};
      }
    };

    const unsubscribeAll = setupRealtimePayments();

    return unsubscribeAll;
  }, []);

  // ✅ Calculate cumulative amounts for each payment to show payment progression
  useEffect(() => {
    if (firebasePayments.length > 0 && patientsBillingData.size > 0) {
      console.log('🔄 Calculating cumulative payment progression...');
      
      // Group payments by patient
      const paymentsByPatient = new Map<string, Payment[]>();
      firebasePayments.forEach(payment => {
        if (!paymentsByPatient.has(payment.patientId)) {
          paymentsByPatient.set(payment.patientId, []);
        }
        paymentsByPatient.get(payment.patientId)!.push(payment);
      });
      
      // Process each patient's payments
      const updatedPayments: Payment[] = [];
      paymentsByPatient.forEach((patientPayments, patientId) => {
        const patientBilling = patientsBillingData.get(patientId);
        
        // Sort payments chronologically (newest first)
        const sortedPayments = [...patientPayments].sort((a, b) => {
          const dateA = new Date(a.lastPaymentDate).getTime();
          const dateB = new Date(b.lastPaymentDate).getTime();
          return dateB - dateA; // Reverse order: newest first
        });
        
        // Calculate cumulative amounts for each payment (in reverse, since newest is first)
        let cumulativePaid = 0;
        // First calculate total for all payments
        const totalPaidAllTransactions = sortedPayments
          .filter(p => p.status !== 'failed' && p.status !== 'refunded')
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        
        // Process from newest to oldest, calculating cumulative from newest
        sortedPayments.forEach((payment, index) => {
          const totalAmount = patientBilling?.totalAmount || payment.totalAmount || payment.amount || 0;
          
          // Calculate cumulative paid from all transactions AFTER this one (including this one)
          const paymentsFromThisPoint = sortedPayments.slice(index);
          const cumulativeFromHere = paymentsFromThisPoint
            .filter(p => p.status !== 'failed' && p.status !== 'refunded')
            .reduce((sum, p) => sum + (p.amount || 0), 0);
          
          const dueAmount = Math.max(0, totalAmount - cumulativeFromHere);
          
          // ✅ When patient due = 0, ALL payment cards show dueAmount = 0
          const patientDueAmount = patientBilling?.dueAmount ?? dueAmount;
          const finalDueAmount = patientDueAmount === 0 ? 0 : dueAmount;
          
          // Individual payment status logic:
          // - Each payment that was successfully made shows as "PAID" (green)
          // - Only show "Fully Paid" when entire patient bill is paid (due = 0)
          // - Due amount shows accurate remaining balance
          let displayStatus = payment.status;
          let displayPaymentStatus = payment.paymentStatus;
          let displayStatusColor = payment.statusColor;
          
          const statusLower = (payment.status || '').toLowerCase();
          const isSuccessfulPayment = 
            statusLower === 'paid' || 
            statusLower === 'fully paid' || 
            statusLower === 'success' || 
            statusLower === 'completed' ||
            statusLower === 'partial' ||
            statusLower === 'partially paid';
          
          if (finalDueAmount === 0) {
            // Patient fully paid - show as "Fully Paid"
            displayStatus = 'Fully Paid';
            displayPaymentStatus = 'Fully Paid';
            displayStatusColor = '#22C55E';
          } else if (isSuccessfulPayment && payment.amount > 0) {
            // This individual payment was made successfully - show as "PAID"
            displayStatus = 'Paid';
            displayPaymentStatus = 'Paid';
            displayStatusColor = '#22C55E'; // Green - payment was successful
          } else if (statusLower === 'pending') {
            // Pending payment
            displayStatus = 'Pending';
            displayPaymentStatus = 'Pending';
            displayStatusColor = '#EF4444'; // Red for pending
          }
          
          updatedPayments.push({
            ...payment,
            totalAmount: totalAmount,              // Patient's total bill (constant)
            paidAmount: cumulativeFromHere,        // Cumulative paid up to this transaction
            dueAmount: finalDueAmount,             // ✅ 0 for ALL cards when patient due = 0
            status: displayStatus,                 // Display status (auto-updated)
            paymentStatus: displayPaymentStatus,   // Display payment status
            statusColor: displayStatusColor        // Display color
          });
        });
      });
      
      setPayments(updatedPayments);
      console.log('✅ Payment progression calculated');
    } else if (firebasePayments.length > 0) {
      setPayments(firebasePayments);
    }
  }, [firebasePayments, patientsBillingData]);

  // Payments page shows individual transaction data only
  // Each payment card displays its own transaction amount independently
  // Cumulative billing (Total Amount, Total Paid, Due Amount) is shown in Patients page > Payment Invoices tab

  // Filtered payments (matching React Native filtering logic)
  const filteredPayments = useMemo(() => {
    // Merge regular payments with appointment payments for display
    const allPayments = [...payments, ...appointmentPayments];
    
    // Special handling for "Due Patients" filter - show only ONE card per patient with due > 0
    if (selectedFilter === 'Due Patients') {
      const patientsWithDues = new Map<string, Payment>();
      
      allPayments.forEach(payment => {
        // ✅ ENHANCED: Only include patients with actual due amount > 0
        const hasDueAmount = payment.dueAmount !== undefined && 
                            payment.dueAmount !== null && 
                            payment.dueAmount > 0;
        
        if (hasDueAmount) {
          // Keep only the LATEST payment card for each patient
          if (!patientsWithDues.has(payment.patientId)) {
            patientsWithDues.set(payment.patientId, payment);
          } else {
            // Compare dates - keep the newer/latest one
            const existing = patientsWithDues.get(payment.patientId)!;
            const existingDate = new Date(existing.lastPaymentDate || existing.registrationDate || 0).getTime();
            const currentDate = new Date(payment.lastPaymentDate || payment.registrationDate || 0).getTime();
            
            // Always keep the latest (newest) payment card
            if (currentDate > existingDate) {
              patientsWithDues.set(payment.patientId, payment);
            }
          }
        }
      });
      
      // ✅ ENHANCED: Calculate Last Bill amount for each patient
      const uniquePatientsWithLastBill = Array.from(patientsWithDues.values()).map(payment => {
        // Find the most recent payment record for this patient to get last bill amount
        const patientPayments = allPayments
          .filter(p => p.patientId === payment.patientId)
          .sort((a, b) => {
            const dateA = new Date(a.lastPaymentDate || a.registrationDate || 0).getTime();
            const dateB = new Date(b.lastPaymentDate || b.registrationDate || 0).getTime();
            return dateB - dateA; // Most recent first
          });
        
        const lastBillAmount = patientPayments.length > 0 ? patientPayments[0].amount : payment.amount;
        
        return {
          ...payment,
          lastBillAmount: lastBillAmount || 0 // Add last bill amount
        };
      });
      
      // Apply search filter and ensure due > 0
      const filteredPatients = uniquePatientsWithLastBill.filter(payment => {
        const hasActiveDue = payment.dueAmount > 0; // Double check due amount > 0
        const matchesSearch = !searchQuery || 
               payment.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
               payment.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
               payment.originalId.toLowerCase().includes(searchQuery.toLowerCase());
        
        return hasActiveDue && matchesSearch;
      });
      
      // Sort by date (newest first) to show most recent dues at top
      return filteredPatients.sort((a, b) => {
        const dateA = new Date(a.lastPaymentDate || a.registrationDate || 0).getTime();
        const dateB = new Date(b.lastPaymentDate || b.registrationDate || 0).getTime();
        return dateB - dateA; // Newest first
      });
    }
    
    // For all other filters, use standard filtering
    return allPayments.filter(payment => {
      // Search filtering
      const matchesSearch = !searchQuery || 
                           payment.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           payment.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           payment.originalId.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Status and Service Type filtering
      const paymentStatus = payment.status.toLowerCase();
      let matchesFilter = false;
      
      if (selectedFilter === 'All') {
        matchesFilter = true;
      } 
      else if (selectedFilter === 'Fully Paid') {
        matchesFilter = paymentStatus === 'fully paid';
      } else if (selectedFilter === 'Partially Paid') {
        matchesFilter = paymentStatus === 'partially paid';
      } else if (selectedFilter === 'Pending') {
        matchesFilter = paymentStatus === 'pending';
      } 
      else if (selectedFilter === 'OP Payments') {
        matchesFilter = payment.serviceType === 'OP';
      } else if (selectedFilter === 'IP Payments') {
        matchesFilter = payment.serviceType === 'IP';
      } else if (selectedFilter === 'Test Payments') {
        matchesFilter = payment.serviceType === 'TEST';
      } else if (selectedFilter === 'General') {
        matchesFilter = payment.serviceType === 'General' || payment.type === 'consultation';
      } 
      
      return matchesSearch && matchesFilter;
    });
  }, [payments, appointmentPayments, searchQuery, selectedFilter]);

  // Handler functions (matching React Native functionality)
  const handleViewDetails = (payment: Payment) => {
    // Check if this is an unknown patient (from Razorpay payments without patient linking)
    if (payment.patientName === 'Unknown Patient' || payment.patientId.startsWith('unknown-')) {
      setSnackbar({ 
        open: true, 
        message: 'This payment is not linked to a patient. You can link it to an existing patient or create a new patient record.', 
        severity: 'warning' 
      });
      // Still show the modal so user can see payment details
      setSelectedPayment(payment);
      setShowDetailModal(true);
    } else {
      setSelectedPayment(payment);
      setShowDetailModal(true);
    }
  };

  const handleEditPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    // TODO: Implement edit payment modal
    setSnackbar({ open: true, message: 'Edit payment functionality coming soon', severity: 'success' });
  };

  const handleAddPayment = () => {
    setShowAddModal(true);
  };

  // Enhanced functionality handlers (matching PatientsEnhanced invoice functionality)
  const handleViewInvoice = (payment: Payment) => {
    // Create a detailed invoice view in a new window
    const invoiceWindow = window.open('', '_blank', 'width=800,height=600');
    if (invoiceWindow) {
      const invoiceHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice - ${payment.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .info { margin: 20px 0; }
            .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
            .items { margin: 20px 0; }
            .items table { width: 100%; border-collapse: collapse; }
            .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .items th { background-color: #f2f2f2; }
            .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
            .breakdown { margin: 15px 0; }
            .breakdown-item { display: flex; justify-content: space-between; padding: 5px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>KBR LIFE CARE</h1>
            <h2>Payment Invoice</h2>
          </div>
          <div class="info">
            <div class="info-row"><strong>Invoice ID:</strong> <span>${payment.id}</span></div>
            <div class="info-row"><strong>Patient Name:</strong> <span>${payment.patientName}</span></div>
            <div class="info-row"><strong>Patient ID:</strong> <span>${payment.patientId}</span></div>
            <div class="info-row"><strong>Date:</strong> <span>${payment.lastPaymentDate}</span></div>
            <div class="info-row"><strong>Transaction ID:</strong> <span>${payment.transactionId || 'N/A'}</span></div>
            <div class="info-row"><strong>Payment Method:</strong> <span>${(payment.paymentMethod || payment.method).toUpperCase()}</span></div>
            <div class="info-row"><strong>Status:</strong> <span>${payment.status.toUpperCase()}</span></div>
            <div class="info-row"><strong>Service Type:</strong> <span>${payment.serviceType} - ${payment.patientType}</span></div>
          </div>
          <div class="items">
            <h3>Payment Details</h3>
            ${payment.billBreakdown && payment.billBreakdown.length > 0 ? `
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${payment.billBreakdown.map(item => `
                    <tr>
                      <td>${item.item}</td>
                      <td>₹${item.amount.toLocaleString()}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : `<p>${payment.description}</p>`}
          </div>
          <div class="breakdown">
            <div class="breakdown-item"><strong>Total Amount:</strong> <span>₹${payment.totalAmount.toLocaleString()}</span></div>
            <div class="breakdown-item"><strong>Paid Amount:</strong> <span>₹${payment.paidAmount.toLocaleString()}</span></div>
            <div class="breakdown-item" style="color: ${payment.dueAmount > 0 ? '#EF4444' : '#22C55E'};"><strong>Due Amount:</strong> <span>₹${payment.dueAmount.toLocaleString()}</span></div>
          </div>
        </body>
        </html>
      `;
      invoiceWindow.document.write(invoiceHTML);
      invoiceWindow.document.close();
    }
  };

  const handleShareInvoice = async (payment: Payment) => {
    const invoiceText = `
KBR LIFE CARE - Payment Invoice
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Invoice ID: ${payment.id}
Patient: ${payment.patientName}
Date: ${payment.lastPaymentDate}
Total Amount: ₹${payment.totalAmount.toLocaleString()}
Paid Amount: ₹${payment.paidAmount.toLocaleString()}
Due Amount: ₹${payment.dueAmount.toLocaleString()}
Payment Method: ${(payment.paymentMethod || payment.method).toUpperCase()}
Status: ${payment.status.toUpperCase()}
${payment.transactionId ? `Transaction ID: ${payment.transactionId}` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice - ${payment.id}`,
          text: invoiceText
        });
        setSnackbar({ 
          open: true, 
          message: 'Invoice shared successfully', 
          severity: 'success' 
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          setSnackbar({ 
            open: true, 
            message: 'Failed to share invoice', 
            severity: 'error' 
          });
        }
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(invoiceText);
        setSnackbar({ 
          open: true, 
          message: 'Invoice copied to clipboard', 
          severity: 'success' 
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to copy invoice', 
          severity: 'error' 
        });
      }
    }
  };

  const handleDownloadInvoice = (payment: Payment) => {
    // Create invoice HTML content
    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice - ${payment.id}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
    .info { margin: 20px 0; }
    .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
    .items { margin: 20px 0; }
    .items table { width: 100%; border-collapse: collapse; }
    .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    .items th { background-color: #f2f2f2; }
    .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
    .breakdown { margin: 15px 0; }
    .breakdown-item { display: flex; justify-content: space-between; padding: 5px 0; }
  </style>
</head>
<body>
  <div class="header">
    <h1>KBR LIFE CARE</h1>
    <h2>Payment Invoice</h2>
  </div>
  <div class="info">
    <div class="info-row"><strong>Invoice ID:</strong> <span>${payment.id}</span></div>
    <div class="info-row"><strong>Patient Name:</strong> <span>${payment.patientName}</span></div>
    <div class="info-row"><strong>Patient ID:</strong> <span>${payment.patientId}</span></div>
    <div class="info-row"><strong>Date:</strong> <span>${payment.lastPaymentDate}</span></div>
    <div class="info-row"><strong>Transaction ID:</strong> <span>${payment.transactionId || 'N/A'}</span></div>
    <div class="info-row"><strong>Payment Method:</strong> <span>${(payment.paymentMethod || payment.method).toUpperCase()}</span></div>
    <div class="info-row"><strong>Status:</strong> <span>${payment.status.toUpperCase()}</span></div>
    <div class="info-row"><strong>Service Type:</strong> <span>${payment.serviceType} - ${payment.patientType}</span></div>
  </div>
  <div class="items">
    <h3>Payment Details</h3>
    ${payment.billBreakdown && payment.billBreakdown.length > 0 ? `
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          ${payment.billBreakdown.map(item => `
            <tr>
              <td>${item.item}</td>
              <td>₹${item.amount.toLocaleString()}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    ` : `<p>${payment.description}</p>`}
  </div>
  <div class="breakdown">
    <div class="breakdown-item"><strong>Total Amount:</strong> <span>₹${payment.totalAmount.toLocaleString()}</span></div>
    <div class="breakdown-item"><strong>Paid Amount:</strong> <span>₹${payment.paidAmount.toLocaleString()}</span></div>
    <div class="breakdown-item" style="color: ${payment.dueAmount > 0 ? '#EF4444' : '#22C55E'};"><strong>Due Amount:</strong> <span>₹${payment.dueAmount.toLocaleString()}</span></div>
  </div>
</body>
</html>
    `.trim();

    // Create a Blob from the HTML content
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Invoice_${payment.patientName.replace(/\s+/g, '_')}_${payment.id}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSnackbar({ 
      open: true, 
      message: 'Invoice downloaded successfully', 
      severity: 'success' 
    });
  };

  const handleCollectPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    
    // Determine patient type based on existing payment data
    let patientType = 'user'; // default
    if (payment.serviceType === 'IP' || payment.patientType === 'IP') {
      patientType = 'ip-op';
    } else if (payment.serviceType === 'OP' || payment.patientType === 'OP') {
      // Check if patient exists in users collection or appointments/patients
      // For now, default to ip-op for OP patients as they're from appointments
      patientType = 'ip-op';
    }
    
    // Pre-populate form with payment details - matching mobile app
    setAddPaymentForm({
      patientType: patientType,
      patientId: payment.patientId,
      patientName: payment.patientName,
      fullAmount: payment.totalAmount.toString(),
      actualAmountPaid: payment.dueAmount.toString(), // Suggest collecting the due amount
      type: payment.type || 'consultation',
      paymentMethod: 'cash',
      description: payment.description || `Additional payment for ${payment.patientName}`,
      transactionId: '',
      razorpayPaymentId: '',
      razorpayOrderId: '',
      razorpaySignature: ''
    });
    
    setShowAddModal(true);
    setSnackbar({ 
      open: true, 
      message: `Adding payment for ${payment.patientName} - Due: ₹${payment.dueAmount.toLocaleString()}`, 
      severity: 'info' 
    });
  };

  const formatCurrency = (amount: number) => {
    return `₹${(amount || 0).toLocaleString('en-IN')}`;
  };

  // Handle patient search and show patient cards
  const handlePatientSearch = (query: string) => {
    setSearchQuery(query);
    
    if (query.length >= 2) {
      // Search in both users and patients collections
      const allPatients = [...users, ...ipOpPatients];
      const filtered = allPatients.filter(patient => 
        patient.name.toLowerCase().includes(query.toLowerCase()) ||
        patient.phone.includes(query) ||
        patient.id.toLowerCase().includes(query.toLowerCase())
      );
      
      // Remove duplicates based on ID
      const uniquePatients = filtered.filter((patient, index, self) => 
        index === self.findIndex(p => p.id === patient.id)
      );
      
      setSearchedPatients(uniquePatients.slice(0, 5)); // Show max 5 results
      setShowPatientCards(true);
    } else {
      setSearchedPatients([]);
      setShowPatientCards(false);
    }
  };

  // Handle Add Payment from patient card
  const handleAddPaymentFromCard = async (patient: User) => {
    console.log('🎯 Opening Add Payment for patient from search card:', patient.name);
    
    // Fetch patient's billing data
    const billingData = await getPatientBillingData(patient.id);
    setPatientBillingSummary(billingData);
    
    // Pre-fill the form with patient data
    setAddPaymentForm({
      patientType: 'ip-op', // Default type
      patientId: patient.id,
      patientName: patient.name,
      fullAmount: billingData.totalAmount > 0 ? billingData.totalAmount.toString() : '',
      actualAmountPaid: '',
      type: 'consultation',
      paymentMethod: 'cash',
      description: `Payment for ${patient.name}`,
      transactionId: '',
      razorpayPaymentId: '',
      razorpayOrderId: '',
      razorpaySignature: ''
    });
    
    // Hide patient cards and show Add Payment modal
    setShowPatientCards(false);
    setSearchQuery('');
    setSearchedPatients([]);
    setShowAddModal(true);
  };

  const selectUser = async (user: User) => {
    // Fetch patient's billing data from payments collection
    const patientBillingData = await getPatientBillingData(user.id);
    
    // Store billing summary for display
    setPatientBillingSummary(patientBillingData);
    
    setAddPaymentForm(prev => ({
      ...prev,
      patientId: user.id,
      patientName: user.name,
      // Pre-populate with existing data if available
      fullAmount: patientBillingData.totalAmount > 0 ? patientBillingData.totalAmount.toString() : '',
      actualAmountPaid: '' // Leave empty for user to enter current payment
    }));
    setShowUserSelector(false);
    setSearchTerm('');
  };

  // Function to get patient's total billing summary from all payments
  // ✅ EXACT SAME LOGIC as PatientsEnhanced.tsx for consistency
  const getPatientBillingData = async (patientId: string) => {
    try {
      // Query all payments for this patient - EXACT same query as PatientsEnhanced
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('patientId', '==', patientId)
      );
      
      const snapshot = await getDocs(paymentsQuery);
      
      if (snapshot.empty) {
        console.log(`⚠️ No payment records found for patient ${patientId}`);
        return { totalAmount: 0, totalPaid: 0, dueAmount: 0 };
      }
      
      console.log(`📋 Found ${snapshot.docs.length} payment records for patient ${patientId}`);
      console.log('Raw Firebase docs:', snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() })));
      
      // Parse payment records EXACTLY like PatientsEnhanced does
      const paymentRecords = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Processing document:', doc.id, data);
        
        let totalAmt = parseFloat(data.totalAmount || data.fullAmount || 0);
        const paidAmt = parseFloat(data.paidAmount || data.actualAmountPaid || 0);
        const amount = parseFloat(data.amount || 0);
        let dueAmt = parseFloat(data.dueAmount || data.remainingAmount || 0);
        
        console.log('Initial values:', { totalAmt, paidAmt, amount, dueAmt });
        
        // ✅ CRITICAL FIX: If totalAmount is 0 but we have paidAmount and dueAmount, calculate it
        if (totalAmt === 0 && (paidAmt > 0 || dueAmt > 0)) {
          totalAmt = paidAmt + dueAmt;
          console.log(`🔧 Calculated totalAmount from paidAmount (${paidAmt}) + dueAmount (${dueAmt}) = ${totalAmt}`);
        }
        
        // ✅ ADDITIONAL FIX: If still 0, but amount exists, assume amount is part of a larger total
        if (totalAmt === 0 && amount > 0) {
          // Look for any other fields that might contain the total
          const possibleTotals = [
            data.billAmount, data.invoiceAmount, data.grandTotal, 
            data.finalAmount, data.originalAmount, data.totalBill
          ].filter(val => val && parseFloat(val) > 0);
          
          if (possibleTotals.length > 0) {
            totalAmt = parseFloat(possibleTotals[0]);
            console.log(`🔧 Found total in alternative field: ${totalAmt}`);
          } else {
            // Last resort: if this is a registration payment, use a reasonable calculation
            if (paidAmt > 0 && dueAmt > 0) {
              totalAmt = paidAmt + dueAmt;
            } else if (paidAmt > 0) {
              // Estimate total based on payment pattern (common scenario: paid 300, total likely 500)
              totalAmt = Math.max(paidAmt, amount);
              console.log(`🔧 Estimated total from payment: ${totalAmt}`);
            }
          }
        }
        
        // Recalculate dueAmount based on totalAmount and paidAmount
        dueAmt = Math.max(0, totalAmt - paidAmt);
        
        console.log('💾 Payment record:', {
          id: doc.id,
          totalAmount: totalAmt,
          paidAmount: paidAmt,
          amount: amount,
          dueAmount: dueAmt,
          raw: {
            totalAmount: data.totalAmount,
            fullAmount: data.fullAmount,
            paidAmount: data.paidAmount,
            dueAmount: data.dueAmount
          }
        });
        
        return {
          totalAmount: totalAmt,
          paidAmount: paidAmt,
          amount: amount,
          dueAmount: dueAmt,
          status: data.status
        };
      });
      
      // ✅ EXACT same calculation as PatientsEnhanced (lines 4786-4803)
      // Get the MAXIMUM totalAmount from all payment records
      const maxTotalAmount = paymentRecords.reduce((max, payment) => {
        const total = payment.totalAmount || 0;
        return total > max ? total : max;
      }, 0);
      
      // ✅ CRITICAL: Add fallback like PatientsEnhanced does
      // If maxTotalAmount is 0, try to get from patients collection
      let totalAmount = maxTotalAmount;
      if (totalAmount === 0) {
        // Check if we have billing data from patients collection
        const patientBilling = patientsBillingData.get(patientId);
        if (patientBilling && patientBilling.totalAmount > 0) {
          totalAmount = patientBilling.totalAmount;
          console.log(`🔧 Using totalAmount from patients collection: ${totalAmount}`);
        } else {
          // Last resort: calculate from the payment record with the highest paidAmount + dueAmount
          const recordWithMostData = paymentRecords
            .filter(p => (p.paidAmount || 0) > 0 || (p.dueAmount || 0) > 0)
            .sort((a, b) => (b.paidAmount + b.dueAmount) - (a.paidAmount + a.dueAmount))[0];
          
          if (recordWithMostData) {
            totalAmount = recordWithMostData.paidAmount + recordWithMostData.dueAmount;
            console.log(`🔧 Calculated totalAmount from best record: paid=${recordWithMostData.paidAmount} + due=${recordWithMostData.dueAmount} = ${totalAmount}`);
          }
        }
      }
      
      // Calculate ACTUAL paid amount by summing all individual payment amounts
      const totalPaid = paymentRecords
        .filter(p => p.status !== 'failed' && p.status !== 'refunded')
        .reduce((sum, payment) => sum + (payment.amount || 0), 0);
      
      const dueAmount = Math.max(0, totalAmount - totalPaid);
      
      console.log(`📊 Patient ${patientId} billing SUMMARY:`, { 
        totalAmount, 
        totalPaid, 
        dueAmount,
        paymentRecords: paymentRecords.length
      });
      
      return { totalAmount, totalPaid, dueAmount };
    } catch (error) {
      console.error('Error fetching patient billing data:', error);
      return { totalAmount: 0, totalPaid: 0, dueAmount: 0 };
    }
  };

  const handleRazorpayPayment = async () => {
    console.log('💳 Initiating Razorpay payment...');
    console.log('🔍 Current State - processingPayment:', processingPayment, 'paymentCompleted:', paymentCompleted);
    
    // CRITICAL: Prevent duplicate payments
    if (processingPayment) {
      console.log('⚠️ Payment already in progress, preventing duplicate');
      setSnackbar({
        open: true,
        message: 'Payment is already being processed. Please wait...',
        severity: 'warning'
      });
      return;
    }
    
    if (paymentCompleted) {
      console.log('✅ Payment already completed, proceeding with submission instead of payment');
      setSnackbar({
        open: true,
        message: 'Payment already completed. Submitting payment record...',
        severity: 'info'
      });
      handleAddPaymentSubmit();
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      // Validate payment amount
      const fullAmount = parseFloat(addPaymentForm.fullAmount) || 0;
      const actualPaid = parseFloat(addPaymentForm.actualAmountPaid || addPaymentForm.fullAmount) || 0;
      
      if (actualPaid <= 0) {
        setSnackbar({
          open: true,
          message: 'Payment amount must be greater than 0',
          severity: 'error'
        });
        setProcessingPayment(false);
        return;
      }
      
      // Prepare payment data for Razorpay
      const paymentData = {
        amount: actualPaid,
        patientId: addPaymentForm.patientId,
        patientName: addPaymentForm.patientName,
        patientEmail: '', // Can be enhanced later
        patientPhone: '', // Can be enhanced later
        paymentType: addPaymentForm.type,
        description: addPaymentForm.description || `Payment for ${addPaymentForm.patientName}`
      };
      
      console.log('💰 Payment data prepared:', paymentData);
      
      // Check Razorpay availability first
      const availability = RazorpayService.testRazorpayAvailability();
      if (!availability.available) {
        setSnackbar({
          open: true,
          message: `Razorpay Error: ${availability.error}`,
          severity: 'error'
        });
        setProcessingPayment(false);
        return;
      }
      
      // Initiate Razorpay payment
      await RazorpayService.initiatePayment(
        paymentData,
        // Success callback
        async (response: RazorpayResponse) => {
          console.log('✅ Razorpay payment successful:', response);
          
          try {
            // Verify payment data
            if (!response.razorpay_payment_id) {
              throw new Error('Invalid payment response - missing payment ID');
            }
            
            // CRITICAL: Mark payment as completed FIRST to prevent duplicates
            setPaymentCompleted(true);
            setProcessingPayment(false);
            
            // Store payment details in form
            setRazorpayOrderId(response.razorpay_order_id || '');
            setAddPaymentForm(prev => ({
              ...prev,
              transactionId: response.razorpay_payment_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id || '',
              razorpaySignature: response.razorpay_signature || ''
            }));
            
            console.log('💾 Payment details stored successfully');
            console.log('🔒 Payment marked as completed - will NOT trigger again');
            
            setSnackbar({
              open: true,
              message: `✅ Payment Confirmed! ₹${actualPaid.toLocaleString()} - Click Add Payment button to complete`,
              severity: 'success'
            });
            
            // DO NOT auto-proceed - let user click the button
            console.log('👆 User must click "Add Payment" button to proceed');
            
          } catch (confirmError: any) {
            console.error('❌ Error confirming payment:', confirmError);
            setProcessingPayment(false);
            setSnackbar({
              open: true,
              message: `Payment successful but confirmation failed: ${confirmError.message}`,
              severity: 'warning'
            });
          }
        },
        // Failure callback
        (error: RazorpayError) => {
          console.error('❌ Razorpay payment failed:', error);
          
          setProcessingPayment(false);
          setPaymentCompleted(false);
          
          let errorMessage = 'Payment failed';
          
          if (error.code === 'BAD_REQUEST_ERROR') {
            errorMessage = 'Payment was cancelled by user';
          } else if (error.description) {
            errorMessage = error.description;
          } else if (error.reason) {
            errorMessage = error.reason;
          } else {
            errorMessage = 'Payment process was interrupted';
          }
          
          setSnackbar({
            open: true,
            message: `❌ ${errorMessage}. Please try again or use a different payment method.`,
            severity: 'error'
          });
          
          console.log('🔄 Payment failed, user can retry or use different method');
        }
      );
      
    } catch (error: any) {
      console.error('❌ Payment initiation error:', error);
      setProcessingPayment(false);
      setPaymentCompleted(false);
      
      setSnackbar({
        open: true,
        message: `Payment failed: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleAddPaymentSubmit = async () => {
    try {
      console.log('📋 Payment submission initiated...');
      console.log('Payment Completed Status:', paymentCompleted);
      console.log('Processing Payment Status:', processingPayment);
      
      // CRITICAL: Prevent re-triggering payment if already in progress or completed
      // Check if payment method is Razorpay and handle payment first
      if (addPaymentForm.paymentMethod === 'razorpay') {
        if (!paymentCompleted && !processingPayment) {
          console.log('💳 Razorpay selected but payment not completed, initiating payment...');
          await handleRazorpayPayment();
          return; // Exit here, payment success will continue submission
        } else if (processingPayment) {
          console.log('⚠️ Payment already in progress, skipping duplicate request');
          return; // Prevent duplicate payment processing
        } else {
          console.log('✅ Razorpay payment already completed, proceeding with submission');
        }
      }
      
      // Validate form - matching mobile app validation
      if (!addPaymentForm.patientType) {
        setSnackbar({ open: true, message: 'Please select patient type', severity: 'error' });
        return;
      }

      if (!addPaymentForm.patientId || !addPaymentForm.patientName || !addPaymentForm.fullAmount) {
        setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
        return;
      }

      const fullAmount = parseFloat(addPaymentForm.fullAmount);
      const actualPaid = parseFloat(addPaymentForm.actualAmountPaid || addPaymentForm.fullAmount);
      
      // Ensure actualPaid doesn't exceed fullAmount
      const validActualPaid = Math.min(actualPaid, fullAmount);
      const dueAmount = Math.max(0, fullAmount - validActualPaid); // Ensure never negative
      
      if (actualPaid > fullAmount) {
        setSnackbar({ 
          open: true, 
          message: `Payment amount (₹${actualPaid}) exceeds total amount (₹${fullAmount}). Adjusting to ₹${fullAmount}.`, 
          severity: 'warning' 
        });
      }
      
      // Determine status based on actual payment - matching mobile app logic
      let status = 'Pending';
      let statusColor = '#EF4444';
      if (validActualPaid === 0) {
        status = 'Pending';
        statusColor = '#EF4444';
      } else if (dueAmount === 0) {
        status = 'Fully Paid';
        statusColor = '#22C55E';
      } else {
        status = 'Partially Paid';
        statusColor = '#F59E0B';
      }

      // Determine service type - matching mobile app logic
      let serviceType = 'OP';
      let patientType = 'OP';
      if (addPaymentForm.type === 'admission' || addPaymentForm.type === 'surgery') {
        serviceType = 'IP';
        patientType = 'IP';
      } else if (addPaymentForm.type === 'tests') {
        serviceType = 'TEST';
        patientType = 'TEST';
      }

      // Check if we're updating an existing payment or creating a new one
      if (selectedPayment && selectedPayment.id) {
        // UPDATING EXISTING PAYMENT
        console.log('🔄 Updating existing payment record:', selectedPayment.id);
        
        // Calculate new totals based on existing payment
        const existingPaidAmount = selectedPayment.paidAmount || 0;
        const existingTotalAmount = parseFloat(addPaymentForm.fullAmount); // Use updated total if changed
        
        // Validate that new payment doesn't exceed remaining due
        const remainingDue = Math.max(0, existingTotalAmount - existingPaidAmount);
        const paymentToAdd = Math.min(actualPaid, remainingDue); // Don't allow overpayment
        
        if (actualPaid > remainingDue && remainingDue > 0) {
          setSnackbar({ 
            open: true, 
            message: `Payment amount (₹${actualPaid}) exceeds remaining due (₹${remainingDue}). Adjusting to ₹${remainingDue}.`, 
            severity: 'warning' 
          });
        }
        
        if (remainingDue === 0) {
          setSnackbar({ 
            open: true, 
            message: 'This payment is already fully paid. No additional payment needed.', 
            severity: 'info' 
          });
          return;
        }
        
        const newTotalPaid = existingPaidAmount + paymentToAdd;
        const newDueAmount = Math.max(0, existingTotalAmount - newTotalPaid); // Ensure never negative
        
        // Determine new status
        let newStatus = 'Pending';
        let newStatusColor = '#EF4444';
        if (newDueAmount === 0) {
          newStatus = 'Fully Paid';
          newStatusColor = '#22C55E';
        } else if (newTotalPaid > 0) {
          newStatus = 'Partially Paid';
          newStatusColor = '#F59E0B';
        }
        
        // Create transaction history entry
        const newTransaction = {
          type: addPaymentForm.type,
          amount: paymentToAdd,
          date: new Date().toISOString().split('T')[0],
          method: addPaymentForm.paymentMethod,
          status: 'Completed',
          transactionId: addPaymentForm.transactionId || null
        };
        
        // Get existing transaction history and add new one
        const existingTransactions = selectedPayment.transactionHistory || [];
        const updatedTransactions = [...existingTransactions, newTransaction];
        
        // Update payment in Firebase
        const paymentRef = doc(db, 'payments', selectedPayment.id);
        await updateDoc(paymentRef, {
          totalAmount: existingTotalAmount,
          fullAmount: existingTotalAmount,
          paidAmount: newTotalPaid,
          actualAmountPaid: newTotalPaid,
          amount: newTotalPaid,
          dueAmount: newDueAmount,
          remainingAmount: newDueAmount,
          status: newStatus,
          paymentStatus: newStatus,
          statusColor: newStatusColor,
          lastPaymentDate: new Date().toISOString().split('T')[0],
          transactionHistory: updatedTransactions,
          updatedAt: new Date().toISOString()
        });
        
        console.log('✅ Payment updated successfully:', selectedPayment.id);
        setSnackbar({ 
          open: true, 
          message: `Payment updated! Paid: ₹${paymentToAdd.toLocaleString()}, Total Paid: ₹${newTotalPaid.toLocaleString()}, Due: ₹${newDueAmount.toLocaleString()}`, 
          severity: 'success' 
        });
      } else {
        // CREATING NEW PAYMENT
        console.log('➕ Creating new payment record');
        
        // Determine if this is updating existing patient billing or extra service
        let finalTotalAmount = fullAmount;
        let finalPaidAmount = validActualPaid;
        let finalDueAmount = dueAmount;
        let isExtraServicePayment = false;
        
        if (patientBillingSummary && patientBillingSummary.totalAmount > 0) {
          // Patient has existing billing records
          
          if (fullAmount > patientBillingSummary.totalAmount) {
            // Admin is UPDATING the total amount (e.g., from 500 to 1000)
            // This means new services/charges were added
            finalTotalAmount = fullAmount; // Use the new total
            finalPaidAmount = patientBillingSummary.totalPaid + validActualPaid; // Cumulative paid
            finalDueAmount = Math.max(0, finalTotalAmount - finalPaidAmount);
            
            console.log('📝 Admin updated total amount:');
            console.log('  - Previous Total:', patientBillingSummary.totalAmount);
            console.log('  - New Total:', finalTotalAmount);
            console.log('  - Already Paid:', patientBillingSummary.totalPaid);
            console.log('  - New Payment:', validActualPaid);
            console.log('  - Total Paid Now:', finalPaidAmount);
            console.log('  - Remaining Due:', finalDueAmount);
          } else if (patientBillingSummary.dueAmount === 0 && fullAmount === patientBillingSummary.totalAmount) {
            // Patient fully paid, no change in total = Extra service payment
            isExtraServicePayment = true;
            finalTotalAmount = patientBillingSummary.totalAmount + fullAmount;
            finalPaidAmount = patientBillingSummary.totalPaid + validActualPaid;
            finalDueAmount = Math.max(0, finalTotalAmount - finalPaidAmount);
            
            console.log('💡 Extra service payment detected:');
            console.log('  - Previous Total:', patientBillingSummary.totalAmount);
            console.log('  - Service Amount:', fullAmount);
            console.log('  - New Total:', finalTotalAmount);
            console.log('  - Total Paid:', finalPaidAmount);
          } else {
            // Regular payment against existing due - KEEP THE ORIGINAL TOTAL
            finalTotalAmount = patientBillingSummary.totalAmount; // ✅ PRESERVE original total
            finalPaidAmount = patientBillingSummary.totalPaid + validActualPaid;
            finalDueAmount = Math.max(0, finalTotalAmount - finalPaidAmount);
            
            console.log('💰 Regular payment against existing due:');
            console.log('  - Original Total:', patientBillingSummary.totalAmount);
            console.log('  - Keeping Total:', finalTotalAmount);
            console.log('  - Already Paid:', patientBillingSummary.totalPaid);
            console.log('  - New Payment:', validActualPaid);
            console.log('  - Total Paid:', finalPaidAmount);
            console.log('  - Due:', finalDueAmount);
          }
        }
        
        const newPayment = {
          patientId: addPaymentForm.patientId,
          patientName: addPaymentForm.patientName,
          amount: validActualPaid, // Actual amount paid in THIS transaction
          fullAmount: finalTotalAmount, // Total bill amount
          totalAmount: finalTotalAmount,
          actualAmountPaid: validActualPaid, // This transaction's payment
          paidAmount: finalPaidAmount, // Cumulative paid amount across all transactions
          dueAmount: finalDueAmount, // Remaining balance
          remainingAmount: finalDueAmount,
          type: addPaymentForm.type,
          method: addPaymentForm.paymentMethod,
          paymentMethod: addPaymentForm.paymentMethod,
          description: addPaymentForm.description + (isExtraServicePayment ? ' (Extra Service)' : ''),
          isExtraService: isExtraServicePayment || false,
          // ✅ Only add transactionId for Razorpay payments
          ...(addPaymentForm.paymentMethod === 'razorpay' && addPaymentForm.transactionId ? { transactionId: addPaymentForm.transactionId } : {}),
          status: status,
          paymentStatus: status,
          statusColor: statusColor,
          patientType: patientType,
          serviceType: serviceType,
          lastPaymentDate: new Date().toISOString().split('T')[0],
          paymentDate: new Date().toISOString().split('T')[0],
          registrationDate: new Date().toISOString().split('T')[0],
          transactionHistory: [{
            type: addPaymentForm.type,
            amount: validActualPaid,
            date: new Date().toISOString().split('T')[0],
            method: addPaymentForm.paymentMethod,
            status: 'Completed',
            isExtraService: isExtraServicePayment || false,
            // ✅ Only add transactionId for Razorpay
            ...(addPaymentForm.paymentMethod === 'razorpay' && addPaymentForm.transactionId ? { transactionId: addPaymentForm.transactionId } : {})
          }],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        // Add to Firebase
        const docRef = await addDoc(collection(db, 'payments'), newPayment);
        console.log('✅ Payment added with ID:', docRef.id);
        
        const successMessage = isExtraServicePayment 
          ? `Extra service payment added! New Total: ₹${finalTotalAmount.toLocaleString()}, Total Paid: ₹${finalPaidAmount.toLocaleString()}`
          : 'Payment added successfully!';
        
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
      }
      
      
      setShowAddModal(false);
      setSelectedPayment(null); // Reset selected payment
      setPatientBillingSummary(null); // Reset billing summary
      setPaymentCompleted(false);
      setProcessingPayment(false);
      setRazorpayOrderId('');
      setAddPaymentForm({
        patientType: '',
        patientId: '',
        patientName: '',
        fullAmount: '',
        actualAmountPaid: '',
        type: 'consultation',
        paymentMethod: 'cash',
        description: '',
        transactionId: '',
        razorpayPaymentId: '',
        razorpayOrderId: '',
        razorpaySignature: ''
      });
    } catch (error) {
      console.error('Error adding payment:', error);
      setSnackbar({ open: true, message: 'Failed to add payment', severity: 'error' });
    }
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Reset all filters to show all data
      setSearchQuery('');
      setSelectedFilter('All');
      
      // TODO: Implement actual data refresh from Firebase
      console.log('✅ Payments data refreshed');
    } catch (error) {
      console.error('❌ Error refreshing payments data:', error);
      setSnackbar({ open: true, message: 'Failed to refresh payments data. Please try again.', severity: 'error' });
    } finally {
      setRefreshing(false);
    }
  }, []);

  const handleGenerateInvoice = (payment: Payment) => {
    // TODO: Implement invoice generation
    setSnackbar({ open: true, message: 'Invoice generation functionality coming soon', severity: 'success' });
  };

  // Filter menu handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filterValue: string) => {
    setSelectedFilter(filterValue);
    setFilterAnchorEl(null);
  };

  // StatCard component (matching React Native style)
  const StatCard: React.FC<{ title: string; value: string | number; color: string; icon: React.ReactNode; subtitle?: string }> = ({ 
    title, value, color, icon, subtitle 
  }) => (
    <Card sx={{ 
      backgroundColor: color,
      color: 'white',
      borderRadius: 3,
      minHeight: 120
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: 2, 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '24px', lineHeight: 1 }}>
              {typeof value === 'number' && title.includes('₹') ? `₹${value.toLocaleString()}` : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ fontSize: '12px', opacity: 0.8, mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '14px' }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  // PaymentCard component (MOBILE APP STYLE - matching React Native design)
  const PaymentCard: React.FC<{ payment: Payment }> = ({ payment }) => {
    const getPaymentMethodIcon = (method: string) => {
      switch (method.toLowerCase()) {
        case 'cash': return <CashIcon sx={{ fontSize: 18, color: '#10B981' }} />;
        case 'card': return <CardIcon sx={{ fontSize: 18, color: '#3B82F6' }} />;
        case 'bank': return <BankIcon sx={{ fontSize: 18, color: '#8B5CF6' }} />;
        default: return <PaymentIcon sx={{ fontSize: 18, color: '#6B7280' }} />;
      }
    };

    const getServiceTypeIcon = (serviceType: string) => {
      switch (serviceType) {
        case 'OP': return <PersonIcon sx={{ fontSize: 20, color: '#3B82F6' }} />;
        case 'IP': return <HospitalIcon sx={{ fontSize: 20, color: '#EF4444' }} />;
        case 'TEST': return <TestIcon sx={{ fontSize: 20, color: '#8B5CF6' }} />;
        default: return <PaymentIcon sx={{ fontSize: 20, color: '#6B7280' }} />;
      }
    };

    const getStatusColor = (status: string) => {
      const statusLower = status.toLowerCase();
      if (statusLower === 'fully paid' || statusLower === 'paid') return '#10B981';
      if (statusLower === 'partially paid' || statusLower === 'partial') return '#F59E0B';
      if (statusLower === 'pending') return '#EF4444';
      return '#6B7280';
    };

    return (
      <Card sx={{ 
        mb: 2.5, 
        borderRadius: 3, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid #E5E7EB',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        }
      }}>
        <CardContent sx={{ p: 3 }}>
          {/* Payment Header - Matching Mobile App */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Avatar sx={{ 
                bgcolor: '#4A90E2', 
                width: 56, 
                height: 56, 
                fontSize: '20px', 
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(74,144,226,0.3)'
              }}>
                {payment.patientName.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '18px', 
                  color: '#1F2937',
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {payment.patientName}
                  {/* Warning icon for unknown patients */}
                  {(payment.patientName === 'Unknown Patient' || payment.patientId.startsWith('unknown-')) && (
                    <Chip 
                      label="NO PATIENT LINKED" 
                      size="small"
                      sx={{ 
                        backgroundColor: '#FEF3C7',
                        color: '#92400E',
                        fontSize: '10px',
                        height: '20px',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    color: '#6B7280', 
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <PersonIcon sx={{ fontSize: 14 }} />
                    {payment.patientId}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#D1D5DB' }}>•</Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#6B7280', 
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    {getServiceTypeIcon(payment.serviceType)}
                    {payment.serviceType}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              <Chip 
                label={payment.status} 
                sx={{ 
                  backgroundColor: getStatusColor(payment.status), 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: '12px',
                  height: '28px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                size="small"
              />
              {/* Service Type Badge - MATCHING MOBILE APP */}
              {payment.serviceType && (
                <Chip 
                  label={payment.serviceType} 
                  sx={{ 
                    backgroundColor: payment.serviceType === 'OP' ? '#E0F2FE' : 
                                   payment.serviceType === 'IP' ? '#FEF3C7' : 
                                   payment.serviceType === 'TEST' ? '#F3E8FF' : '#F1F5F9',
                    color: payment.serviceType === 'OP' ? '#0369A1' : 
                           payment.serviceType === 'IP' ? '#D97706' : 
                           payment.serviceType === 'TEST' ? '#7C3AED' : '#475569',
                    fontWeight: 'bold',
                    fontSize: '10px',
                    height: '24px',
                    textTransform: 'uppercase',
                    border: '1px solid',
                    borderColor: payment.serviceType === 'OP' ? '#BAE6FD' : 
                                 payment.serviceType === 'IP' ? '#FDE68A' : 
                                 payment.serviceType === 'TEST' ? '#E9D5FF' : '#CBD5E1'
                  }}
                  size="small"
                />
              )}
              {payment.isLocalPayment && (
                <Chip 
                  label="NEW" 
                  sx={{ 
                    backgroundColor: '#10B981', 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: '11px',
                    height: '24px'
                  }}
                  size="small"
                />
              )}
            </Box>
          </Box>
          
          {/* Payment Amounts Grid - Enhanced for Due Patients */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr 1fr', 
              sm: selectedFilter === 'Due Patients' ? '1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr' 
            },
            gap: 2,
            mb: 3,
            p: 2.5,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Box>
              <Typography variant="caption" sx={{ 
                color: '#6B7280', 
                fontSize: '11px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}>
                Total Amount
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#1F2937', 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                ₹{payment.totalAmount.toLocaleString()}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" sx={{ 
                color: '#6B7280', 
                fontSize: '11px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}>
                Amount Paid
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#10B981', 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                ₹{payment.paidAmount.toLocaleString()}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" sx={{ 
                color: '#6B7280', 
                fontSize: '11px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}>
                Due Amount
              </Typography>
              <Typography variant="h6" sx={{ 
                color: payment.dueAmount > 0 ? '#EF4444' : '#10B981', 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                ₹{payment.dueAmount.toLocaleString()}
              </Typography>
            </Box>
            
            {/* Show Last Bill Amount only for Due Patients filter */}
            {selectedFilter === 'Due Patients' && (
              <Box>
                <Typography variant="caption" sx={{ 
                  color: '#6B7280', 
                  fontSize: '11px', 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  mb: 0.5
                }}>
                  Last Bill
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: '#8B5CF6', 
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}>
                  ₹{((payment as any).lastBillAmount || payment.amount).toLocaleString()}
                </Typography>
              </Box>
            )}
            
            <Box>
              <Typography variant="caption" sx={{ 
                color: '#6B7280', 
                fontSize: '11px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}>
                Payments Made
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#4A90E2', 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                {payment.paymentHistory?.length || 1}
              </Typography>
            </Box>
          </Box>

          {/* Payment History Section - EXACT MATCH TO MOBILE APP */}
          <Box sx={{ 
            mb: 3, 
            p: 2.5, 
            backgroundColor: '#F9FAFB', 
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              color: '#374151', 
              fontWeight: 600, 
              mb: 2,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <ReceiptIcon sx={{ fontSize: 16, color: '#4A90E2' }} />
              Payment History
            </Typography>
            
            {/* Payment History Details */}
            {payment.paymentHistory && payment.paymentHistory.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {payment.paymentHistory.slice(0, 3).map((historyPayment: any, historyIndex: number) => {
                  const sequence = historyIndex + 1;
                  const suffix = sequence === 1 ? 'st' : sequence === 2 ? 'nd' : sequence === 3 ? 'rd' : 'th';
                  
                  return (
                    <Box key={`${historyPayment.id}-${historyIndex}`} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 1.5,
                      backgroundColor: '#FFFFFF',
                      borderRadius: 1.5,
                      border: '1px solid #E5E7EB'
                    }}>
                      <Box sx={{ 
                        minWidth: '60px',
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: '#EFF6FF',
                        borderRadius: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="caption" sx={{ 
                          color: '#3B82F6', 
                          fontWeight: 600,
                          fontSize: '11px'
                        }}>
                          {sequence}{suffix} time
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          color: '#1F2937', 
                          fontWeight: 600,
                          fontSize: '14px'
                        }}>
                          ₹{(historyPayment.amount || 0).toLocaleString()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px' }}>
                            {historyPayment.date || historyPayment.paidDate || new Date(historyPayment.createdAt || Date.now()).toLocaleDateString('en-IN')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#D1D5DB' }}>•</Typography>
                          <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px' }}>
                            {historyPayment.method || historyPayment.paymentMethod || 'Cash'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                
                {payment.paymentHistory.length > 3 && (
                  <Button
                    variant="text"
                    size="small"
                    sx={{ 
                      color: '#4A90E2', 
                      fontSize: '12px',
                      textTransform: 'none',
                      alignSelf: 'flex-start',
                      px: 1
                    }}
                  >
                    +{payment.paymentHistory.length - 3} more payments
                  </Button>
                )}
              </Box>
            ) : (
              // Single payment or no history
              <Box>
                {payment.paidAmount > 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1.5,
                    backgroundColor: '#FFFFFF',
                    borderRadius: 1.5,
                    border: '1px solid #E5E7EB'
                  }}>
                    <Box sx={{ 
                      minWidth: '60px',
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: '#EFF6FF',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        color: '#3B82F6', 
                        fontWeight: 600,
                        fontSize: '11px'
                      }}>
                        1st time
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#1F2937', 
                        fontWeight: 600,
                        fontSize: '14px'
                      }}>
                        ₹{(payment.paidAmount || payment.amount || 0).toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px' }}>
                          {payment.lastPaymentDate || 'Recently'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#D1D5DB' }}>•</Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px' }}>
                          {payment.method || payment.paymentMethod || 'Cash'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '13px', fontStyle: 'italic' }}>
                    No payments made yet
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Payment Status Summary - EXACT MATCH TO MOBILE APP */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
              <Typography variant="body2" sx={{ 
                color: payment.dueAmount <= 0 ? '#10B981' : 
                       payment.paidAmount > 0 ? '#F59E0B' : '#EF4444',
                fontWeight: 600,
                fontSize: '13px'
              }}>
                Status: {payment.dueAmount <= 0 ? 'Fully Paid' : 
                         payment.paidAmount > 0 ? `Partially Paid (₹${payment.dueAmount.toLocaleString()} due)` : 
                         'Payment Pending'}
              </Typography>
            </Box>
          </Box>

          {/* Local Payment Indicator - Matching Mobile App */}
          {payment.isLocalPayment && payment.localIndicator && (
            <Box sx={{ 
              mb: 2, 
              p: 1.5, 
              backgroundColor: '#FEF3C7', 
              borderRadius: 1.5,
              border: '1px solid #FDE68A',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <WarningIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
              <Typography variant="body2" sx={{ 
                color: '#92400E', 
                fontStyle: 'italic',
                fontSize: '12px',
                fontWeight: 500
              }}>
                {payment.localIndicator}
              </Typography>
            </Box>
          )}

          {/* Action Buttons - Matching Mobile App Functionality */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5, 
            flexWrap: 'wrap', 
            pt: 2.5, 
            borderTop: '2px solid #F3F4F6' 
          }}>
            {/* Show Link Patient button for unknown patients */}
            {(payment.patientName === 'Unknown Patient' || payment.patientId.startsWith('unknown-')) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonIcon />}
                onClick={() => {
                  setSelectedPayment(payment);
                  setSnackbar({ 
                    open: true, 
                    message: 'Feature coming soon: Link this payment to an existing patient or create a new patient record', 
                    severity: 'info' 
                  });
                }}
                sx={{ 
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#D97706'
                  }
                }}
              >
                Link to Patient
              </Button>
            )}
            
            {/* Show collect payment button for pending or partial payments - Matching Mobile App */}
            {(payment.dueAmount > 0 || payment.status === 'Pending' || payment.status === 'Partially Paid') && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleCollectPayment(payment)}
                sx={{ 
                  color: '#10B981', 
                  borderColor: '#10B981',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#ECFDF5',
                    borderColor: '#059669'
                  }
                }}
              >
                Add Payment
              </Button>
            )}
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewIcon />}
              onClick={() => handleViewInvoice(payment)}
              sx={{ 
                color: '#3B82F6', 
                borderColor: '#3B82F6',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  backgroundColor: '#EFF6FF',
                  borderColor: '#2563EB'
                }
              }}
            >
              View
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadInvoice(payment)}
              sx={{ 
                color: '#8B5CF6', 
                borderColor: '#8B5CF6',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  backgroundColor: '#F5F3FF',
                  borderColor: '#7C3AED'
                }
              }}
            >
              Download
            </Button>
            
            <Button
              variant="text"
              size="small"
              startIcon={<ShareIcon />}
              onClick={() => handleShareInvoice(payment)}
              sx={{ 
                color: '#6B7280',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  backgroundColor: '#F9FAFB'
                }
              }}
            >
              Share
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Payment Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Track payment transactions and manage billing
        </Typography>
      </Box>

      {/* Statistics Cards - Matching Mobile Design */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
        gap: 2,
        mb: 4 
      }}>
        {/* Statistics Cards - MATCHING MOBILE APP LAYOUT */}
        <Card sx={{ backgroundColor: '#E8F5E8', borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '13px' }}>
              Total Revenue
            </Typography>
            <Typography variant="h5" sx={{ color: '#22C55E', fontWeight: 'bold', mb: 0.5 }}>
              ₹{paymentStats.totalRevenue.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
              {paymentStats.fullyPaidCount} fully paid
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ backgroundColor: '#FFF3CD', borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '13px' }}>
              Pending Dues
            </Typography>
            <Typography variant="h5" sx={{ color: '#EF4444', fontWeight: 'bold', mb: 0.5 }}>
              ₹{paymentStats.totalPending.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
              {paymentStats.pendingCount} pending
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ backgroundColor: '#E0E7FF', borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '13px' }}>
              Partially Paid
            </Typography>
            <Typography variant="h5" sx={{ color: '#8B5CF6', fontWeight: 'bold', mb: 0.5 }}>
              {paymentStats.partiallyPaidCount}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
              patients
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ backgroundColor: '#F3F4F6', borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '13px' }}>
              Total Patients
            </Typography>
            <Typography variant="h5" sx={{ color: '#4A90E2', fontWeight: 'bold', mb: 0.5 }}>
              {paymentStats.totalPatients}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
              with payments
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        position: 'relative'
      }}>
        <Box sx={{ flex: 1, position: 'relative' }} data-search-container>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search patients by name, phone, or ID to add payment..."
            value={searchQuery}
            onChange={(e) => handlePatientSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#9CA3AF' }} />,
            }}
          />
          
          {/* Patient Cards Dropdown */}
          {showPatientCards && searchedPatients.length > 0 && (
            <Paper sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: 400,
              overflow: 'auto',
              mt: 1,
              border: '1px solid #E5E7EB'
            }}>
              <List>
                {searchedPatients.map((patient) => (
                  <ListItem key={patient.id} sx={{ borderBottom: '1px solid #F3F4F6' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      width: '100%',
                      p: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#10B981', width: 40, height: 40 }}>
                          {patient.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {patient.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            ID: {patient.id.slice(0, 8)}... | Phone: {patient.phone}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddPaymentFromCard(patient)}
                        sx={{
                          backgroundColor: '#10B981',
                          '&:hover': { backgroundColor: '#059669' },
                          borderRadius: 2
                        }}
                      >
                        Add Payment
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={handleFilterClick}
          sx={{ minWidth: 180 }}
        >
          {selectedFilter}
        </Button>
        
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
        >
          {filterOptions.map((option) => (
            <MenuItem 
              key={option} 
              onClick={() => handleFilterSelect(option)}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPayment}
          sx={{ 
            backgroundColor: '#10B981', 
            '&:hover': { backgroundColor: '#059669' },
            borderRadius: 3
          }}
        >
          Add Payment
        </Button>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      {/* Payments List */}
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredPayments.length === 0 ? (
          <Card sx={{ 
            p: 6, 
            textAlign: 'center',
            backgroundColor: '#F9FAFB',
            border: '2px dashed #E5E7EB'
          }}>
            <PaymentIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h5" sx={{ color: '#374151', mb: 2, fontWeight: 600 }}>
              {searchQuery || selectedFilter !== 'All' 
                ? 'No payments found'
                : 'No payments recorded yet'
              }
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B7280', mb: 4 }}>
              {searchQuery || selectedFilter !== 'All' 
                ? 'Try adjusting your search or filter criteria to find payments'
                : 'Get started by adding your first payment record'
              }
            </Typography>
            {!searchQuery && selectedFilter === 'All' && (
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleAddPayment}
                sx={{ 
                  backgroundColor: '#10B981', 
                  '&:hover': { backgroundColor: '#059669' },
                  borderRadius: 3,
                  px: 4,
                  py: 1.5
                }}
              >
                Add Your First Payment
              </Button>
            )}
          </Card>
        ) : (
          filteredPayments.map((payment) => (
            <PaymentCard key={payment.id} payment={payment} />
          ))
        )}
      </Box>

      {/* Add Payment Modal - Matching Mobile Form */}
      <Dialog
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedPayment(null);
          setPatientBillingSummary(null);
          setPaymentCompleted(false);
          setProcessingPayment(false);
          setRazorpayOrderId('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #F3F4F6',
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#1F2937'
        }}>
          {selectedPayment ? `Add Payment - ${selectedPayment.patientName}` : 'Add Payment Record'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Show existing payment info if updating */}
            {selectedPayment && (
              <Box sx={{ p: 2, bgcolor: '#F0F9FF', borderRadius: 1, border: '1px solid #3B82F6' }}>
                <Typography variant="body2" sx={{ color: '#1E40AF', fontWeight: 600, mb: 1 }}>
                  💵 Existing Payment Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Total Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                      ₹{selectedPayment.totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Already Paid</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#22C55E' }}>
                      ₹{selectedPayment.paidAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Due Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#EF4444' }}>
                      ₹{selectedPayment.dueAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Payments Made</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B82F6' }}>
                      {selectedPayment.transactionHistory?.length || 1}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Patient Type Selection - FIRST */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                Patient Type *
              </Typography>
              <TextField
                select
                fullWidth
                value={addPaymentForm.patientType}
                onChange={(e) => {
                  setAddPaymentForm(prev => ({ 
                    ...prev, 
                    patientType: e.target.value,
                    patientId: '',
                    patientName: ''
                  }));
                  setPatientBillingSummary(null); // Reset billing summary when changing patient type
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Patient Type</option>
                <option value="user">Registered User</option>
                <option value="ip-op">IP/OP Patient</option>
              </TextField>
            </Box>

            {/* User Selection - Show only if patient type is selected */}
            {addPaymentForm.patientType === 'user' && (
              <TextField
                label={`Select User * (${users.length} available)`}
                value={addPaymentForm.patientName || ''}
                onClick={() => setShowUserSelector(true)}
                placeholder="Click to select user"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ cursor: 'pointer' }}
              />
            )}

            {/* IP/OP Patient Selection */}
            {addPaymentForm.patientType === 'ip-op' && (
              <TextField
                label={`Select IP/OP Patient * (${ipOpPatients.length} available)`}
                value={addPaymentForm.patientName || ''}
                onClick={() => setShowUserSelector(true)}
                placeholder="Click to select IP/OP patient"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ cursor: 'pointer' }}
              />
            )}

            {/* Show selected patient info */}
            {addPaymentForm.patientName && (
              <Box sx={{ p: 2, bgcolor: '#EEF2FF', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#4F46E5', fontWeight: 600 }}>
                  <strong>Selected Patient:</strong> {addPaymentForm.patientName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  Patient ID: {addPaymentForm.patientId}
                </Typography>
              </Box>
            )}

            {/* Patient Billing Summary - Show when patient is selected */}
            {addPaymentForm.patientName && patientBillingSummary && (
              <Box sx={{ 
                p: 2.5, 
                bgcolor: '#F0FDF4', 
                borderRadius: 2, 
                border: '2px solid #22C55E',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)'
              }}>
                <Typography variant="body2" sx={{ color: '#15803D', fontWeight: 700, mb: 2, fontSize: '15px' }}>
                  💰 Patient Payment Summary
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px', display: 'block', mb: 0.5 }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#1F2937', fontWeight: 700, fontSize: '18px' }}>
                      ₹{patientBillingSummary.totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px', display: 'block', mb: 0.5 }}>
                      Total Paid
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#22C55E', fontWeight: 700, fontSize: '18px' }}>
                      ₹{patientBillingSummary.totalPaid.toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '11px', display: 'block', mb: 0.5 }}>
                      Due Amount
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      color: patientBillingSummary.dueAmount > 0 ? '#EF4444' : '#22C55E', 
                      fontWeight: 700,
                      fontSize: '18px'
                    }}>
                      ₹{patientBillingSummary.dueAmount.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                {/* Show message for extra service payments */}
                {patientBillingSummary.dueAmount === 0 && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#DBEAFE', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1E40AF', fontSize: '13px', fontWeight: 600 }}>
                      ℹ️ This patient has no pending dues. Any new payment will be added as an <strong>extra service</strong>.
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1E3A8A', display: 'block', mt: 0.5, fontSize: '11px' }}>
                      The new payment will increase both Total Amount and Total Paid by the same value.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Patient Information - Now read-only, auto-filled */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                Patient Information *
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Patient ID"
                  value={addPaymentForm.patientId}
                  InputProps={{ readOnly: true }}
                  required
                  sx={{ bgcolor: '#F9FAFB' }}
                />
                <TextField
                  fullWidth
                  label="Patient Name"
                  value={addPaymentForm.patientName}
                  InputProps={{ readOnly: true }}
                  required
                  sx={{ bgcolor: '#F9FAFB' }}
                />
              </Box>
            </Box>

            {/* Amount Section - MATCHING MOBILE APP */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                {selectedPayment ? 'Payment Details *' : 'Payment Amounts *'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={selectedPayment ? "Total Amount (Editable)" : "Full Amount"}
                  type="number"
                  value={addPaymentForm.fullAmount}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, fullAmount: e.target.value })}
                  placeholder="Enter total amount in ₹"
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                  }}
                />
                <TextField
                  fullWidth
                  label={selectedPayment ? "Additional Payment Amount" : "Actual Amount Paid"}
                  type="number"
                  value={addPaymentForm.actualAmountPaid || (selectedPayment ? '' : addPaymentForm.fullAmount)}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, actualAmountPaid: e.target.value })}
                  placeholder={selectedPayment ? "Enter additional payment in ₹" : "Enter amount actually paid in ₹"}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                  }}
                />
              </Box>
              {selectedPayment && addPaymentForm.actualAmountPaid && (
                <Box sx={{ mt: 1, p: 2, backgroundColor: '#EFF6FF', borderRadius: 1, border: '1px solid #3B82F6' }}>
                  <Typography variant="body2" sx={{ color: '#1E40AF', fontWeight: 600 }}>
                    After this payment:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                    Total Paid: ₹{(selectedPayment.paidAmount + parseFloat(addPaymentForm.actualAmountPaid || '0')).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Remaining Due: ₹{Math.max(0, selectedPayment.totalAmount - (selectedPayment.paidAmount + parseFloat(addPaymentForm.actualAmountPaid || '0'))).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: (selectedPayment.paidAmount + parseFloat(addPaymentForm.actualAmountPaid || '0')) >= selectedPayment.totalAmount ? '#22C55E' : '#F59E0B',
                    fontWeight: 600,
                    mt: 0.5
                  }}>
                    New Status: {(selectedPayment.paidAmount + parseFloat(addPaymentForm.actualAmountPaid || '0')) >= selectedPayment.totalAmount ? 'Fully Paid ✅' : 'Partially Paid'}
                  </Typography>
                </Box>
              )}
              {!selectedPayment && addPaymentForm.fullAmount && addPaymentForm.actualAmountPaid && (
                <Box sx={{ mt: 1, p: 2, backgroundColor: '#F9FAFB', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Due Amount: ₹{(parseFloat(addPaymentForm.fullAmount || '0') - parseFloat(addPaymentForm.actualAmountPaid || '0')).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: parseFloat(addPaymentForm.actualAmountPaid || '0') === parseFloat(addPaymentForm.fullAmount || '0') ? '#22C55E' :
                          parseFloat(addPaymentForm.actualAmountPaid || '0') === 0 ? '#EF4444' : '#F59E0B',
                    fontWeight: 600,
                    mt: 0.5
                  }}>
                    Status: {parseFloat(addPaymentForm.actualAmountPaid || '0') === parseFloat(addPaymentForm.fullAmount || '0') ? 'Fully Paid' :
                             parseFloat(addPaymentForm.actualAmountPaid || '0') === 0 ? 'Pending' : 'Partially Paid'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Payment Type */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                Payment Type
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={addPaymentForm.type}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, type: e.target.value })}
                >
                  <MenuItem value="consultation">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HospitalIcon fontSize="small" />
                      Consultation
                    </Box>
                  </MenuItem>
                  <MenuItem value="tests">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TestIcon fontSize="small" />
                      Laboratory Tests
                    </Box>
                  </MenuItem>
                  <MenuItem value="admission">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      Room/Admission
                    </Box>
                  </MenuItem>
                  <MenuItem value="surgery">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HospitalIcon fontSize="small" />
                      Surgery
                    </Box>
                  </MenuItem>
                  <MenuItem value="medicine">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoneyIcon fontSize="small" />
                      Medicine
                    </Box>
                  </MenuItem>
                  <MenuItem value="emergency">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon fontSize="small" />
                      Emergency
                    </Box>
                  </MenuItem>
                  <MenuItem value="other">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon fontSize="small" />
                      Other
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Payment Method */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                Payment Method
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={addPaymentForm.paymentMethod}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, paymentMethod: e.target.value })}
                >
                  <MenuItem value="cash">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CashIcon fontSize="small" />
                      Cash
                    </Box>
                  </MenuItem>
                  <MenuItem value="razorpay">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon fontSize="small" />
                      Razorpay
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Description */}
            <TextField
              fullWidth
              label="Payment Description *"
              multiline
              rows={3}
              value={addPaymentForm.description}
              onChange={(e) => setAddPaymentForm({ ...addPaymentForm, description: e.target.value })}
              placeholder="e.g., Consultation Fee - Dr. K. Ramesh"
              required
            />

            {/* Transaction ID - Only show for Razorpay */}
            {addPaymentForm.paymentMethod === 'razorpay' && (
              <TextField
                fullWidth
                label="Transaction ID (Optional)"
                value={addPaymentForm.transactionId || ''}
                onChange={(e) => setAddPaymentForm({ ...addPaymentForm, transactionId: e.target.value })}
                placeholder="TXN123456789"
                InputProps={{ readOnly: paymentCompleted }}
                sx={{ bgcolor: paymentCompleted ? '#F9FAFB' : 'transparent' }}
              />
            )}

            {/* Payment Status Indicator */}
            {paymentCompleted && addPaymentForm.razorpayPaymentId && (
              <Box sx={{ p: 2, bgcolor: '#D1FAE5', borderRadius: 1, border: '1px solid #10B981' }}>
                <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Payment Completed Successfully!
                </Typography>
                <Typography variant="caption" sx={{ color: '#047857', display: 'block', mt: 0.5 }}>
                  Payment ID: {addPaymentForm.razorpayPaymentId}
                </Typography>
                <Typography variant="caption" sx={{ color: '#047857', display: 'block' }}>
                  Amount: ₹{parseFloat(addPaymentForm.actualAmountPaid || addPaymentForm.fullAmount || '0').toLocaleString()}
                </Typography>
              </Box>
            )}

            {/* Processing Payment Indicator */}
            {processingPayment && (
              <Box sx={{ p: 2, bgcolor: '#FEF3C7', borderRadius: 1, border: '1px solid #F59E0B' }}>
                <Typography variant="body2" sx={{ color: '#D97706', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#D97706' }} />
                  Processing Razorpay payment...
                </Typography>
                <Typography variant="caption" sx={{ color: '#92400E' }}>
                  Please complete the payment in the Razorpay window
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #F3F4F6' }}>
          <Button 
            onClick={() => {
              setShowAddModal(false);
              setSelectedPayment(null);
              setPatientBillingSummary(null);
              setPaymentCompleted(false);
              setProcessingPayment(false);
              setRazorpayOrderId('');
            }}
            sx={{ color: '#6B7280' }}
            disabled={processingPayment}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPaymentSubmit} 
            variant="contained"
            startIcon={processingPayment ? <CircularProgress size={20} color="inherit" /> : 
                      addPaymentForm.paymentMethod === 'razorpay' && !paymentCompleted ? <PaymentIcon /> : <AddIcon />}
            disabled={processingPayment}
            sx={{ 
              backgroundColor: addPaymentForm.paymentMethod === 'razorpay' && !paymentCompleted ? '#7C3AED' : '#22C55E',
              '&:hover': { backgroundColor: addPaymentForm.paymentMethod === 'razorpay' && !paymentCompleted ? '#6D28D9' : '#16A34A' },
              '&:disabled': { backgroundColor: '#9CA3AF' }
            }}
          >
            {processingPayment ? 'Processing...' : 
             addPaymentForm.paymentMethod === 'razorpay' && !paymentCompleted ? 'Pay with Razorpay' :
             paymentCompleted ? (selectedPayment ? 'Complete Payment Update' : 'Complete Payment Record') : 
             selectedPayment ? 'Update Payment' : 'Add Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Payment Detail Modal (matching React Native design) */}
      <Dialog
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '70vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          backgroundColor: '#F2F2F7', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          Payment Details
          <IconButton onClick={() => setShowDetailModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, backgroundColor: '#F2F2F7' }}>
          {selectedPayment && (
            <Box>
              {/* Invoice Header Card (matching React Native style) */}
              <Card sx={{ 
                m: 3, 
                borderRadius: 4, 
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    <ReceiptIcon sx={{ fontSize: 48, color: '#34C759' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1C1C1E', mb: 1 }}>
                    {selectedPayment.id}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#8E8E93', mb: 2 }}>
                    Date: {selectedPayment.lastPaymentDate}
                  </Typography>
                  <Chip 
                    label={selectedPayment.status}
                    sx={{ 
                      backgroundColor: selectedPayment.statusColor,
                      color: 'white',
                      fontWeight: '600',
                      px: 2,
                      py: 1
                    }}
                  />
                </CardContent>
              </Card>

              {/* Patient Information Section */}
              <Card sx={{ 
                mx: 3, 
                mb: 2, 
                borderRadius: 3,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1C1C1E', mb: 2 }}>
                    Patient Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #F2F2F7' }}>
                    <Typography variant="body2" sx={{ color: '#8E8E93' }}>Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: '500', color: '#1C1C1E' }}>
                      {selectedPayment.patientName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #F2F2F7' }}>
                    <Typography variant="body2" sx={{ color: '#8E8E93' }}>Patient ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: '500', color: '#1C1C1E' }}>
                      {selectedPayment.patientId}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <Typography variant="body2" sx={{ color: '#8E8E93' }}>Type</Typography>
                    <Chip 
                      label={selectedPayment.patientType}
                      size="small"
                      sx={{ 
                        backgroundColor: '#007AFF',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Bill Breakdown Section */}
              <Card sx={{ 
                mx: 3, 
                mb: 2, 
                borderRadius: 3,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1C1C1E', mb: 2 }}>
                    Bill Breakdown
                  </Typography>
                  
                  {/* Bill Items */}
                  {selectedPayment.billBreakdown && selectedPayment.billBreakdown.length > 0 ? (
                    selectedPayment.billBreakdown.map((item, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        py: 1 
                      }}>
                        <Typography variant="body2" sx={{ color: '#1C1C1E', flex: 1 }}>
                          {item.item}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500', color: '#1C1C1E' }}>
                          {formatCurrency(item.amount)}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: '#1C1C1E', flex: 1 }}>
                        {selectedPayment.description}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: '500', color: '#1C1C1E', float: 'right' }}>
                        {formatCurrency(selectedPayment.totalAmount)}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Total Amounts */}
                  <Box sx={{ 
                    borderTop: '1px solid #E5E5EA', 
                    mt: 1, 
                    pt: 1,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1C1C1E' }}>
                      Total Amount:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1C1C1E' }}>
                      {formatCurrency(selectedPayment.totalAmount)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: '600', color: '#34C759' }}>
                      Paid Amount:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#34C759' }}>
                      {formatCurrency(selectedPayment.paidAmount)}
                    </Typography>
                  </Box>
                  
                  {selectedPayment.dueAmount > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 1
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: '600', color: '#FF3B30' }}>
                        Pending Amount:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FF3B30' }}>
                        {formatCurrency(selectedPayment.dueAmount)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Transaction History Section */}
              <Card sx={{ 
                mx: 3, 
                mb: 2, 
                borderRadius: 3,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1C1C1E', mb: 2 }}>
                    Transaction History
                  </Typography>
                  
                  {selectedPayment.transactionHistory && selectedPayment.transactionHistory.length > 0 ? (
                    selectedPayment.transactionHistory.map((transaction, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        py: 1.5,
                        borderBottom: index < selectedPayment.transactionHistory!.length - 1 ? '1px solid #F2F2F7' : 'none'
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: '600', color: '#1C1C1E' }}>
                            {transaction.type}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block', mt: 0.25 }}>
                            {transaction.date}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block' }}>
                            {transaction.method}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#34C759' }}>
                            {formatCurrency(transaction.amount)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block', mt: 0.25 }}>
                            {transaction.status}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: '#8E8E93', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                      No transaction history available
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons (matching React Native style) */}
              <Box sx={{ p: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ViewIcon />}
                  onClick={() => {
                    handleViewInvoice(selectedPayment);
                  }}
                  sx={{ 
                    backgroundColor: '#007AFF',
                    color: 'white',
                    fontWeight: '600',
                    py: 2,
                    mb: 1.5,
                    borderRadius: 3,
                    '&:hover': { backgroundColor: '#0056CC' }
                  }}
                >
                  View Invoice
                </Button>
                
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    handleDownloadInvoice(selectedPayment);
                    setShowDetailModal(false);
                  }}
                  sx={{ 
                    backgroundColor: '#FF3B30',
                    color: 'white',
                    fontWeight: '600',
                    py: 2,
                    mb: 1.5,
                    borderRadius: 3,
                    '&:hover': { backgroundColor: '#E5342C' }
                  }}
                >
                  Download Invoice
                </Button>
                
                {selectedPayment.status === 'Pending' && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={() => {
                      handleCollectPayment(selectedPayment);
                      setShowDetailModal(false);
                    }}
                    sx={{ 
                      backgroundColor: '#34C759',
                      color: 'white',
                      fontWeight: '600',
                      py: 2,
                      mb: 1.5,
                      borderRadius: 3,
                      '&:hover': { backgroundColor: '#2FB344' }
                    }}
                  >
                    Collect Payment
                  </Button>
                )}
                
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => {
                    handleShareInvoice(selectedPayment);
                    setShowDetailModal(false);
                  }}
                  sx={{ 
                    borderColor: '#007AFF',
                    color: '#007AFF',
                    fontWeight: '600',
                    py: 2,
                    borderRadius: 3,
                    '&:hover': { 
                      borderColor: '#0056CC',
                      backgroundColor: 'rgba(0, 122, 255, 0.04)'
                    }
                  }}
                >
                  Share Invoice
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* User Selector Modal */}
      <Dialog open={showUserSelector} onClose={() => { setShowUserSelector(false); setSearchTerm(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {addPaymentForm.patientType === 'ip-op' ? 'Select IP/OP Patient' : 'Select User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {(addPaymentForm.patientType === 'ip-op' ? 
              ipOpPatients.filter(patient => 
                patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                patient.phone.includes(searchTerm) ||
                (patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
              ) : 
              users.filter(user => 
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phone.includes(searchTerm) ||
                (user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
              )
            ).map((user) => (
              <React.Fragment key={user.id}>
                <ListItemButton onClick={() => selectUser(user)}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {user.phone}
                        </Typography>
                        {user.email && (
                          <>
                            {' • '}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
            {(addPaymentForm.patientType === 'ip-op' ? ipOpPatients : users).filter(u => 
              u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              u.phone.includes(searchTerm) ||
              (u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
            ).length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchTerm ? 
                    `No ${addPaymentForm.patientType === 'ip-op' ? 'IP/OP patients' : 'users'} found matching your search` : 
                    `No ${addPaymentForm.patientType === 'ip-op' ? 'IP/OP patients' : 'users'} available`
                  }
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowUserSelector(false); setSearchTerm(''); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;